import { countOccupants } from 'lib/offer/occupancyUtils'

export type OfferPageHighlightTravellerContext = 'family' | 'couple' | 'group' | 'default'

const VIEWED_PROPERTIES_STORAGE_KEY = 'offerPageHighlightTagsViewedProperties'

function hasFamilyOccupancy(rooms: Array<App.Occupants> | undefined) {
  const occupants = countOccupants(rooms ?? [])
  return occupants.children > 0 ||
    occupants.infants > 0 ||
    occupants.teenagers > 0 ||
    occupants.childrenAge.length > 0
}

function hasCoupleOccupancy(rooms: Array<App.Occupants> | undefined) {
  if (rooms?.length !== 1) {
    return false
  }

  const occupants = countOccupants(rooms)
  return occupants.adults === 2 &&
    occupants.children === 0 &&
    occupants.infants === 0 &&
    occupants.teenagers === 0 &&
    occupants.childrenAge.length === 0
}

function hasGroupOccupancy(rooms: Array<App.Occupants> | undefined) {
  const occupants = countOccupants(rooms ?? [])
  const hasChildren = occupants.children > 0 ||
    occupants.infants > 0 ||
    occupants.teenagers > 0 ||
    occupants.childrenAge.length > 0

  return !hasChildren && (
    occupants.adults > 2 ||
    (rooms?.length ?? 0) > 1
  )
}

export function getOfferPageHighlightTravellerContext(
  rooms: Array<App.Occupants> | undefined,
  isOccupancyContextKnown: boolean,
): OfferPageHighlightTravellerContext {
  if (!isOccupancyContextKnown) {
    return 'default'
  }

  if (hasFamilyOccupancy(rooms)) {
    return 'family'
  }

  if (hasCoupleOccupancy(rooms)) {
    return 'couple'
  }

  if (hasGroupOccupancy(rooms)) {
    return 'group'
  }

  return 'default'
}

export function getViewedOfferPageHighlightPropertyIds() {
  if (typeof window === 'undefined') {
    return []
  }

  try {
    const value = window.sessionStorage.getItem(VIEWED_PROPERTIES_STORAGE_KEY)
    const parsedValue = value ? JSON.parse(value) : []
    return Array.isArray(parsedValue) ? parsedValue.filter(item => typeof item === 'string') : []
  } catch {
    return []
  }
}

export function setViewedOfferPageHighlightPropertyIds(propertyIds: Array<string>) {
  if (typeof window === 'undefined') {
    return
  }

  try {
    window.sessionStorage.setItem(VIEWED_PROPERTIES_STORAGE_KEY, JSON.stringify(propertyIds))
  } catch {
    // Storage can fail in private browsing or when disabled; neutral copy is still safe.
  }
}

export function shouldUseFirstNameForNeutralOfferPageHighlight(
  viewedPropertyIds: Array<string>,
) {
  return viewedPropertyIds.length < 2
}
